import capitalize from '@mui/utils/capitalize';
import i18n from 'i18next';

import type { ConfigurationFeaturesResponse } from '@/api/REST/configuration-features';
import type {
  FacetSectionGroup,
  GroupedFacet,
  HoverCardConfig,
  HoverCardFieldItem,
  ResourceConfig,
} from '@/lib/resource-config/types';

export const buildHoverCardFields = (
  hoverCard: HoverCardConfig,
  data: Record<string, unknown> | null | undefined,
): HoverCardFieldItem[] =>
  hoverCard.fields.map((item) => {
    if (item.type === 'row') {
      return {
        ...item,
        fields: item.fields.map((f) => ({
          ...f,
          value: data?.[f.field ?? ''],
          ...(f.entityIdField && {
            entityId: String(data?.[f.entityIdField] ?? ''),
          }),
        })),
      };
    }
    return {
      ...item,
      value: data?.[item.field ?? ''],
      ...(item.entityIdField && {
        entityId: String(data?.[item.entityIdField] ?? ''),
      }),
    };
  });

/**
 * Groups facets by section and sorts them for UI rendering
 *
 * @param config - Resource configuration containing facets and optional section metadata
 * @param enabledFeatures - Optional features from configuration-features endpoint to filter facets
 * @param options - Optional configuration for grouping behavior
 * @param options.hideSingleSection - If true, omits section metadata when only one section exists (default: false)
 * @returns Array of section groups, each containing sorted facets
 *
 * @example
 * const sections = getFacetsBySection(projectsConfig, features);
 * sections.forEach(({ section, label, facets }) => {
 *   console.log(`Section: ${label}`);
 *   facets.forEach(({ key, config }) => {
 *     console.log(`  - ${config.label}`);
 *   });
 * });
 */
export const getFacetsBySection = (
  config: ResourceConfig,
  enabledFeatures?: ConfigurationFeaturesResponse,
  options: { hideSingleSection?: boolean; pagePermissions?: string[] } = {},
): FacetSectionGroup[] => {
  const { hideSingleSection = false, pagePermissions } = options;
  const { facets, facetSections = {} } = config;

  // Group facets by section, preserving insertion order
  const grouped = Object.entries(facets).reduce(
    (acc, [key, facetConfig]) => {
      // Skip if feature flag is disabled
      if (
        facetConfig.featureFlag &&
        enabledFeatures &&
        !enabledFeatures[facetConfig.featureFlag]
      ) {
        return acc;
      }

      // Skip if a required page permission is declared and the user lacks it.
      // pagePermissions undefined = not gating on permissions (legacy callers).
      if (
        facetConfig.requiredPagePermission &&
        pagePermissions &&
        !pagePermissions.includes(facetConfig.requiredPagePermission)
      ) {
        return acc;
      }

      const section = facetConfig.section || 'other';
      if (!acc[section]) {
        acc[section] = [];
      }
      acc[section].push({ key, config: facetConfig });
      return acc;
    },
    {} as Record<string, GroupedFacet[]>,
  );

  // Get the order of sections from facetSections object insertion order
  const sectionOrder = Object.keys(facetSections);

  // Convert to array and sort sections by their definition order
  // Facets within each section maintain their insertion order from the config
  const sections = Object.entries(grouped)
    .map(([sectionKey, facets]) => ({
      section: sectionKey,
      // For 'other' section, use a translated "Other" label if not explicitly defined
      label:
        facetSections[sectionKey]?.label ||
        (sectionKey === 'other' ? i18n.t('Other', { ns: 'ui' }) : sectionKey),
      facets,
    }))
    .sort((a, b) => {
      const aIndex = sectionOrder.indexOf(a.section);
      const bIndex = sectionOrder.indexOf(b.section);
      // Sections not in facetSections go to the end
      if (aIndex === -1 && bIndex === -1) return 0;
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    });

  // If hideSingleSection is enabled and there's only one section,
  // return the section without section metadata (useful for simple cases)
  if (hideSingleSection && sections.length === 1) {
    return [
      {
        ...sections[0],
        section: '',
        label: '',
      },
    ];
  }

  return sections;
};

/**
 * Formats enum values into human-readable labels
 * Attempts to use translations when available, with fallback to basic formatting
 *
 * Translation lookup paths (in order):
 * 1. views.Yes/views.No for boolean values (shared across all resources)
 * 2. {resourceKey}.enums.{value} for other enum values (e.g., "portfolio.enums.InProgress")
 * 3. Default formatting based on value type
 *
 * NOTE: For translations to work, the resourceKey must match the i18n namespace name
 * (e.g., resourceKey: 'portfolio' should have translations in public/locales/{locale}/portfolio.json)
 *
 * @param value - The enum value to format
 * @param resourceKey - The resource key for translation namespace lookup
 * @returns Formatted label string
 */
export function formatEnumLabel(value: string, resourceKey: string): string {
  // Handle boolean values - use shared 'views' namespace
  if (value === 'true' || value === 'false') {
    return value === 'true'
      ? i18n.t('Yes', { ns: 'views' })
      : i18n.t('No', { ns: 'views' });
  }

  // Try to find translation for enum values in resource-specific namespace
  const enumTranslationKey = `enums.${value}`;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  if (i18n.exists(enumTranslationKey, { ns: resourceKey as any })) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return i18n.t(enumTranslationKey, { ns: resourceKey as any });
  }

  // Fallback: capitalize first letter
  return capitalize(value);
}
